import { useLayoutEffect, useRef } from 'react'

// Distance thresholds (px) for "at bottom" and for triggering edge loads.
const NEAR_BOTTOM_PX = 80
const EDGE_PX = 60

// Generic scrollable transcript. Handles, without knowing about the data source:
//  - sticking to the bottom on initial load / new content (only when the user is
//    already near the bottom AND we're at the live end),
//  - bidirectional lazy-loading (scroll up -> older, scroll down -> newer) with
//    scroll-position preservation when older messages are prepended,
//  - jump-to-message: scrolling a highlighted message into view,
//  - a "jump to latest" affordance when the newest messages aren't loaded.
//
// Rendering is injected via `renderItem(message, { highlighted })`.
function Transcript({
  messages,
  renderItem,
  loading,
  loadingLabel = 'Loading…',
  emptyLabel = 'No messages yet.',
  hasMoreBefore,
  hasMoreAfter,
  loadingOlder,
  loadingNewer,
  onLoadOlder,
  onLoadNewer,
  onJumpLatest,
  highlightId,
  footer = null,
  resetKey,
  scrollToBottomSignal = 0,
}) {
  const scrollRef = useRef(null)
  const anchorRef = useRef(null) // { top, height } captured before a prepend
  const atBottomRef = useRef(true)
  const didInitRef = useRef(false)
  const pendingJumpRef = useRef(null)
  const resetKeyRef = useRef(resetKey)
  const scrollSignalRef = useRef(scrollToBottomSignal)

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return

    // Room/source switch: forget follow-state so we snap to the bottom fresh.
    if (resetKeyRef.current !== resetKey) {
      resetKeyRef.current = resetKey
      didInitRef.current = false
      atBottomRef.current = true
      pendingJumpRef.current = null
    }
    if (!highlightId) pendingJumpRef.current = null

    // 1. Older messages were prepended — keep the same content in view.
    if (anchorRef.current) {
      el.scrollTop = anchorRef.current.top + (el.scrollHeight - anchorRef.current.height)
      anchorRef.current = null
      return
    }
    // 2. A new jump target — center it in view.
    if (highlightId && pendingJumpRef.current !== highlightId) {
      pendingJumpRef.current = highlightId
      const node = el.querySelector(`[data-mid="${highlightId}"]`)
      if (node && node.scrollIntoView) node.scrollIntoView({ block: 'center' })
      return
    }
    // 3. Explicit request to jump to the bottom (e.g. after the user sends).
    if (scrollSignalRef.current !== scrollToBottomSignal) {
      scrollSignalRef.current = scrollToBottomSignal
      el.scrollTop = el.scrollHeight
      atBottomRef.current = true
      didInitRef.current = true
      return
    }
    // 4. First render for this source — start at the bottom.
    if (!didInitRef.current) {
      el.scrollTop = el.scrollHeight
      didInitRef.current = true
      return
    }
    // 5. Follow new content only if the user is at the live bottom.
    if (atBottomRef.current && !hasMoreAfter) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages, highlightId, scrollToBottomSignal, resetKey, hasMoreAfter])

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    atBottomRef.current = distanceFromBottom <= NEAR_BOTTOM_PX

    if (el.scrollTop <= EDGE_PX && hasMoreBefore && !loadingOlder && !anchorRef.current) {
      anchorRef.current = { top: el.scrollTop, height: el.scrollHeight }
      onLoadOlder?.()
    }
    if (distanceFromBottom <= EDGE_PX && hasMoreAfter && !loadingNewer) {
      onLoadNewer?.()
    }
  }

  const showEmpty = !loading && messages.length === 0 && !footer

  return (
    <div className="chat-transcript" ref={scrollRef} onScroll={handleScroll}>
      {loading ? (
        <div className="display-empty">{loadingLabel}</div>
      ) : showEmpty ? (
        <div className="display-empty">{emptyLabel}</div>
      ) : (
        <div className="chat-transcript-list">
          {loadingOlder && <div className="chat-transcript-more">Loading earlier messages…</div>}
          {messages.map((message) => (
            <div key={message._id} data-mid={message._id}>
              {renderItem(message, { highlighted: message._id === highlightId })}
            </div>
          ))}
          {loadingNewer && <div className="chat-transcript-more">Loading newer messages…</div>}
          {footer}
        </div>
      )}

      {hasMoreAfter && !loading && (
        <button type="button" className="chat-jump-latest" onClick={onJumpLatest}>
          Jump to latest ↓
        </button>
      )}
    </div>
  )
}

export default Transcript
